import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { SectionHead, ClientsCarousel, Testimonials, Provisional, CtaBand, useList } from "@/components/Blocks";
import { PageHero } from "./About";
import { useSeo } from "@/lib/analytics";
import { useT } from "@/lib/i18n";

export function ClientsPage() {
  const { t } = useT();
  const clients = useList("/public/clients") || [];
  const testimonials = useList("/public/testimonials") || [];
  const cases = useList("/public/case_studies") || [];
  useSeo({ title: "Clientes y casos de éxito", description: "Organizaciones que confían en la tecnología de Cloud Center y resultados reales de nuestros proyectos." });
  return (
    <>
      <PageHero eyebrow="Clientes" title="Resultados reales en operaciones reales" sub="Casos de éxito, testimonios verificados y organizaciones que confían en nuestra tecnología." />
      <section className="wrap py-16 md:py-24">
        <SectionHead eyebrow="Confianza" title={t("trustTitle")} />
        <ClientsCarousel clients={clients} />
      </section>
      <section className="border-y border-slate-200 bg-[color:var(--cc-bg)] py-16 md:py-24">
        <div className="wrap">
          <SectionHead eyebrow="Casos de éxito" title="Del problema operativo al resultado medible" />
          {cases.length ? (
            <div className="grid gap-6 md:grid-cols-2">
              {cases.map((c) => (
                <Link key={c.id} to={`/casos/${c.slug}`} className="card-cc card-cc-hover group" data-testid={`case-card-${c.slug}`}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand">{c.industry}</p>
                  <h3 className="mt-2 font-display text-xl font-semibold">{c.title}</h3>
                  <p className="mt-2 line-clamp-3 text-sm text-slate-600">{c.problem}</p>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-brand">Leer caso <ArrowRight className="h-4 w-4" /></span>
                </Link>
              ))}
            </div>
          ) : (
            <Provisional testid="cases-placeholder" title="Casos de éxito" text="Categorías previstas: automatización empresarial, integraciones, facturación electrónica y aplicaciones móviles. Se publicarán tras la aprobación de cada cliente, sin información confidencial." />
          )}
        </div>
      </section>
      <section className="wrap py-16 md:py-24">
        <SectionHead eyebrow="Testimonios" title={t("testiTitle")} />
        <Testimonials items={testimonials} />
      </section>
      <CtaBand />
    </>
  );
}

const Part = ({ title, text, items }) => (
  <div>
    <p className="eyebrow">{title}</p>
    {text && <p className="mt-3 leading-relaxed text-slate-700 whitespace-pre-line">{text}</p>}
    {items?.length > 0 && <ul className="mt-3 space-y-2">{items.map((x) => <li key={x} className="flex gap-2 text-sm text-slate-700"><Check className="mt-0.5 h-4 w-4 text-brand" />{x}</li>)}</ul>}
  </div>
);

export function CaseDetail() {
  const { slug } = useParams();
  const c = useList(`/public/case_studies/${slug}`);
  const ok = c && !Array.isArray(c);
  useSeo({ title: ok ? c.title : "Caso de éxito", description: ok ? c.problem?.slice(0, 155) : undefined });
  if (c === null) return <div className="wrap py-32 text-slate-500">Cargando…</div>;
  if (!ok) return <div className="wrap py-32 text-center"><h1 className="text-3xl font-bold">Caso no encontrado</h1></div>;
  return (
    <>
      <PageHero eyebrow={c.industry} title={c.title} />
      <section className="wrap grid max-w-4xl gap-10 py-16">
        <Part title="Problema inicial" text={c.problem} />
        <Part title="Solución implementada" text={c.solution} />
        <Part title="Tecnologías utilizadas" items={c.technologies} />
        <Part title="Beneficios obtenidos" items={c.benefits} />
        <Part title="Resultados" items={c.results} />
        {c.images?.map((i) => <img key={i} src={i} alt={c.title} loading="lazy" className="rounded-2xl border border-slate-200" />)}
        {c.testimonial && <blockquote className="card-cc text-lg italic text-slate-700">“{c.testimonial}”</blockquote>}
      </section>
      <CtaBand />
    </>
  );
}
